import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { FiLoader } from 'react-icons/fi';
import { apiService } from '../../components/Config/api.service';
import EmployeeSelect from '../../components/Formulaire/EmployeeSelect';
import { BOUTON_PRINCIPAL, BOUTON_SECONDAIRE, enveloppeDe, stylesRecherche } from './organigrammeOutils';
import SansComptePortail from './SansComptePortail';

/**
 * Action groupée sur une sélection de salariés (Yros/Kavero#469, T3,
 * contrat C29 en commentaire du ticket).
 *
 * La RH choisit un responsable, un département, ou les deux ; « Continuer »
 * demande l'aperçu, « Confirmer » écrit. Le responsable appliqué est celui
 * choisi, à défaut celui du département. Par défaut seules les fiches sans
 * responsable le reçoivent ; la case « Remplacer aussi… » l'applique aux
 * autres. Le département va à toute la sélection.
 */
function AffectationOrganigramme({ employeIds, departements = [], remplacerParDefaut = false, onTermine, onAnnuler }) {
  const { t } = useTranslation();
  const [responsable, setResponsable] = useState(null);
  const [departement, setDepartement] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [remplacer, setRemplacer] = useState(false);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  const corps = () => ({
    employe_ids: employeIds,
    responsable_id: responsable ? responsable.value : null,
    departement_id: departement ? departement.value : null,
  });

  const appeler = async (appel, suite) => {
    setErreur('');
    setEnCours(true);
    const reponse = await enveloppeDe(appel);
    setEnCours(false);
    if (reponse.ok) {
      suite(reponse);
    } else {
      setErreur(reponse.message || t('organigramme.erreurGenerique'));
    }
  };

  const prevoir = () => appeler(
    () => apiService.prevoirAffectationOrganigramme(corps()),
    // Depuis l'organigramme (Yros/Kavero#494), la RH vise une personne : la
    // remplacer est le but, la case part cochée.
    (reponse) => { setRemplacer(remplacerParDefaut); setApercu(reponse); });

  const confirmer = () => appeler(
    () => apiService.appliquerAffectationOrganigramme({ ...corps(), remplacer }),
    (reponse) => onTermine(reponse.modifies));

  if (!apercu) {
    return (
      <div className="space-y-4 text-sm">
        <p className="text-gray-600">{t('organigrammeGroupe.intro', { n: employeIds.length })}</p>
        <div>
          <label className="mb-1 block font-medium text-gray-700">{t('organigrammeGroupe.responsable')}</label>
          <EmployeeSelect value={responsable} onChange={setResponsable} styles={stylesRecherche}
            placeholder={t('organigramme.chercherResponsable')} />
        </div>
        <div>
          <label className="mb-1 block font-medium text-gray-700">{t('organigrammeGroupe.departementLabel')}</label>
          <Select isClearable options={departements} value={departement} onChange={setDepartement}
            styles={stylesRecherche} placeholder={t('organigrammeGroupe.choisirDepartement')} />
        </div>
        <div className="flex items-center gap-2 pt-2">
          <button type="button" className={BOUTON_PRINCIPAL} disabled={(!responsable && !departement) || enCours}
            onClick={prevoir}>
            {enCours && <FiLoader className="animate-spin" />} {t('organigrammeGroupe.continuer')}
          </button>
          <button type="button" className={`${BOUTON_SECONDAIRE} ml-auto`} onClick={onAnnuler}>
            {t('organigramme.annuler')}
          </button>
        </div>
        {erreur && <p role="alert" className="rounded border border-red-200 bg-red-50 p-2 text-red-700">{erreur}</p>}
      </div>
    );
  }

  const cible = apercu.responsable;
  return (
    <div className="space-y-2 text-sm">
      <p className="text-gray-600">{t('organigrammeGroupe.intro', { n: apercu.selection })}</p>
      {apercu.departement && (
        <p className="text-gray-800">{t('organigrammeGroupe.departement', { nom: apercu.departement.nom })}</p>
      )}
      {cible ? (
        <p className="text-gray-800">
          {t('organigrammeGroupe.recoivent', { n: apercu.sans_responsable, nom: cible.nom })}
        </p>
      ) : (
        <p className="text-gray-600">{t('organigrammeGroupe.aucunResponsable')}</p>
      )}
      {cible && apercu.avec_autre_responsable > 0 && (
        <label className="flex cursor-pointer items-center gap-2 pt-1 text-gray-800">
          <input type="checkbox" className="checkbox checkbox-sm checked:bg-[--color-primary] checked:border-[--color-primary]"
            checked={remplacer} onChange={(e) => setRemplacer(e.target.checked)} />
          <span>{t('organigrammeGroupe.remplacer', { n: apercu.avec_autre_responsable })}</span>
        </label>
      )}
      {cible && !cible.compte_portail && <SansComptePortail personne={cible} />}
      <div className="flex items-center gap-2 pt-3">
        <button type="button" className={BOUTON_PRINCIPAL} disabled={enCours} onClick={confirmer}>
          {enCours && <FiLoader className="animate-spin" />} {t('organigrammeGroupe.confirmer')}
        </button>
        <button type="button" className={BOUTON_SECONDAIRE} disabled={enCours}
          onClick={() => { setApercu(null); setErreur(''); }}>
          {t('organigramme.retour')}
        </button>
      </div>
      {erreur && <p role="alert" className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-red-700">{erreur}</p>}
    </div>
  );
}

export default AffectationOrganigramme;
